import React, { useEffect, useState } from 'react';
import {
  Box,
  MenuItem,
  Grid,
  InputAdornment,
  Collapse,
  Chip,
  Alert,
  Autocomplete,
} from '@mui/material';
import {
  SearchOutlined as SearchIcon,
  RefreshOutlined as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { ProductHistoryFilterState } from '../../../types/productHistory.types';
import { ProductHistoryListResponseDto } from '../../../api/models/ProductHistoryListResponseDto';
import { Location } from '../../../api/models/Location';
import { User } from '../../../api/models/User';
import { useProductMovementHistoryFilters } from './ProductMovementHistory.hooks';
import {
  FiltersContainer,
  FiltersGrid,
  SearchTextField,
  StandardTextField,
  SearchButton,
  ResetButton,
  MoreFiltersButton,
  ButtonsContainer,
  ActiveFiltersChip,
  ActiveFiltersContainer,
} from './ProductMovementHistory.styles';

export interface ProductMovementHistoryFiltersProps {
  locations: Location[];
  users: User[];
  /** When false, hide the Usuario Autocomplete (GET /users is settings-gated). */
  canFilterByUser?: boolean;
  onFilterChange: (filters: ProductHistoryFilterState) => void;
  initialFilters?: ProductHistoryFilterState;
}

const getUserDisplayName = (user: User): string => {
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return fullName || user.email || user.id;
};

const EVENT_TYPES: {
  value: ProductHistoryListResponseDto.eventType;
  label: string;
}[] = [
  {
    value: ProductHistoryListResponseDto.eventType.UNIT_ATTRIBUTE_CHANGED,
    label: 'Atributo de pieza cambiado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.AVAILABILITY_STATUS_CHANGED,
    label: 'Cambio de Disponibilidad',
  },
  {
    value: ProductHistoryListResponseDto.eventType.PRODUCT_CATALOG_UPDATED,
    label: 'Catálogo Actualizado',
  },
  { value: ProductHistoryListResponseDto.eventType.STATUS_CHANGED, label: 'Estado Cambiado' },
  {
    value: ProductHistoryListResponseDto.eventType.INVENTORY_REGISTERED,
    label: 'Inventario Registrado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.EXCHANGE_PROCESSED,
    label: 'Intercambio Procesado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.ORDER_DELIVERY_UPDATED,
    label: 'Entrega al cliente',
  },
  { value: ProductHistoryListResponseDto.eventType.ORDER_REFUNDED, label: 'Venta reembolsada' },
  { value: ProductHistoryListResponseDto.eventType.PAYMENT_PROCESSED, label: 'Pago Procesado' },
  { value: ProductHistoryListResponseDto.eventType.PAYMENT_RECEIVED, label: 'Pago Recibido' },
  { value: ProductHistoryListResponseDto.eventType.PRICE_CHANGED, label: 'Precio Cambiado' },
  { value: ProductHistoryListResponseDto.eventType.PRODUCT_CREATED, label: 'Producto Creado' },
  { value: ProductHistoryListResponseDto.eventType.PRODUCT_DELETED, label: 'Producto Eliminado' },
  { value: ProductHistoryListResponseDto.eventType.ITEM_RETURNED, label: 'Producto Devuelto' },
  { value: ProductHistoryListResponseDto.eventType.PROPERTY_ADDED, label: 'Propiedad Agregada' },
  { value: ProductHistoryListResponseDto.eventType.PROPERTY_DELETED, label: 'Propiedad Eliminada' },
  {
    value: ProductHistoryListResponseDto.eventType.PROPERTY_UPDATED,
    label: 'Propiedad Actualizada',
  },
  { value: ProductHistoryListResponseDto.eventType.REFUND_PROCESSED, label: 'Reembolso Procesado' },
  {
    value: ProductHistoryListResponseDto.eventType.ORDER_PARTIALLY_REFUNDED,
    label: 'Reembolso Parcial',
  },
  {
    value: ProductHistoryListResponseDto.eventType.INVENTORY_RECORD_CANCELLED,
    label: 'Registro Cancelado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.INVENTORY_RECORD_COMPLETED,
    label: 'Registro Completado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.INVENTORY_RECORD_CREATED,
    label: 'Registro Creado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.INVENTORY_RECORD_DELETED,
    label: 'Registro Eliminado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.INVENTORY_RECORD_UPDATED,
    label: 'Registro Actualizado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.RESERVATION_CONFIRMED,
    label: 'Reserva Confirmada',
  },
  { value: ProductHistoryListResponseDto.eventType.RESERVATION_CREATED, label: 'Reserva Creada' },
  { value: ProductHistoryListResponseDto.eventType.RESERVATION_EXPIRED, label: 'Reserva Expirada' },
  {
    value: ProductHistoryListResponseDto.eventType.RESERVATION_RELEASED,
    label: 'Reserva Liberada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.TRANSFER_CANCELLED,
    label: 'Transferencia Cancelada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.TRANSFER_COMPLETED,
    label: 'Transferencia Completada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.TRANSFER_CREATED,
    label: 'Transferencia Creada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.REQUEST_CREATED,
    label: 'Solicitud creada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.REQUEST_RESOLVED,
    label: 'Solicitud resuelta',
  },
  { value: ProductHistoryListResponseDto.eventType.LOCATION_CHANGED, label: 'Ubicación Cambiada' },
  { value: ProductHistoryListResponseDto.eventType.SALE_CREATED, label: 'Venta Creada' },
  {
    value: ProductHistoryListResponseDto.eventType.SAFETY_STOCK_UPDATED_INDIVIDUAL,
    label: 'Stock de seguridad (producto)',
  },
  {
    value: ProductHistoryListResponseDto.eventType.SAFETY_STOCK_UPDATED_BULK,
    label: 'Stock de seguridad (edición personalizada)',
  },
  {
    value: ProductHistoryListResponseDto.eventType.PHYSICAL_COUNT_FINALIZED,
    label: 'Control de Inventario Fisico Completado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.ROLE_CREATED,
    label: 'Rol Creado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.ROLE_UPDATED,
    label: 'Rol Actualizado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.ROLE_DELETED,
    label: 'Rol Eliminado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.ROLE_PERMISSIONS_UPDATED,
    label: 'Permisos de Rol Actualizados',
  },
  {
    value: ProductHistoryListResponseDto.eventType.USER_CREATED,
    label: 'Usuario Creado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.USER_UPDATED,
    label: 'Usuario Actualizado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.USER_DELETED,
    label: 'Usuario Eliminado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.USER_ROLES_UPDATED,
    label: 'Roles de Usuario Actualizados',
  },
  {
    value: ProductHistoryListResponseDto.eventType.CUSTOMER_CREATED,
    label: 'Cliente Creado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.CUSTOMER_UPDATED,
    label: 'Cliente Actualizado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.CUSTOMER_DELETED,
    label: 'Cliente Eliminado',
  },
  {
    value: ProductHistoryListResponseDto.eventType.LOGIN_SUCCESS,
    label: 'Inicio de sesión exitoso',
  },
  {
    value: ProductHistoryListResponseDto.eventType.LOGIN_FAILED,
    label: 'Inicio de sesión fallido',
  },
  {
    value: ProductHistoryListResponseDto.eventType.ACCOUNT_LOCKED,
    label: 'Cuenta bloqueada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.ACCOUNT_UNLOCKED,
    label: 'Cuenta desbloqueada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.PASSWORD_RESET_BY_ADMIN,
    label: 'Contraseña restablecida por admin',
  },
  {
    value: ProductHistoryListResponseDto.eventType.PASSWORD_CHANGED,
    label: 'Contraseña cambiada',
  },
  {
    value: ProductHistoryListResponseDto.eventType.FORCED_PASSWORD_CHANGE,
    label: 'Cambio de contraseña obligatorio',
  },
].sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));

const RELATED_ENTITY_TYPES = [
  { value: 'SALES_ORDER', label: 'Gestión de Ventas' },
  { value: 'PRODUCT_CATALOG', label: 'Catálogo de Productos' },
  { value: 'SETTINGS_PROPERTIES', label: 'Propiedades' },
  { value: 'WAREHOUSE_MANAGEMENT', label: 'Gestión de Almacén' },
  { value: 'INVENTORY_TRANSFER', label: 'Transferencia de Inventario' },
  { value: 'INVENTORY_REQUEST', label: 'Solicitud de inventario (SOL)' },
  { value: 'INVENTORY_RECORD', label: 'Registro de Inventario' },
  { value: 'SAFETY_STOCK', label: 'Stock de seguridad (producto)' },
  { value: 'SAFETY_STOCK_BULK', label: 'Stock de seguridad (edición personalizada)' },
  { value: 'PHYSICAL_COUNT', label: 'Control de Inventario Fisico' },
  { value: 'ROLE', label: 'Gestión de Roles' },
  { value: 'USER', label: 'Gestión de Usuarios' },
  { value: 'CUSTOMER_PROFILE', label: 'Gestión de clientes (CRM)' },
];

const ProductMovementHistoryFilters: React.FC<ProductMovementHistoryFiltersProps> = ({
  locations,
  users,
  canFilterByUser = false,
  onFilterChange,
  initialFilters,
}) => {
  const {
    filters,
    showMoreFilters,
    setShowMoreFilters,
    handleChange,
    handleSelectChange,
    handleEventTypeChange,
    handleUserIdChange,
    handleReset,
    handleApply,
    activeFiltersCount,
    dateError,
  } = useProductMovementHistoryFilters(onFilterChange, initialFilters);

  const selectedUser =
    Array.isArray(users) && filters.userId
      ? users.find((user) => user.id === filters.userId) || null
      : null;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleApply();
    }
  };

  return (
    <FiltersContainer>
      <FiltersGrid container spacing={2}>
        <Grid item xs={12}>
          <ButtonsContainer>
            <SearchTextField
              variant="outlined"
              size="medium"
              label="Búsqueda General"
              name="search"
              value={filters.search || ''}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder="Buscar por código de catálogo, número de serie, transferencia, venta o registro"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 300, flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <ResetButton
              variant="outlined"
              size="medium"
              onClick={handleReset}
              startIcon={<RefreshIcon />}
              sx={{
                minWidth: 110,
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'transparent',
                  boxShadow: 1,
                },
              }}
            >
              Reiniciar
            </ResetButton>

            <SearchButton
              variant="contained"
              size="medium"
              onClick={handleApply}
              startIcon={<SearchIcon />}
            >
              Buscar
            </SearchButton>

            <MoreFiltersButton
              variant="outlined"
              size="medium"
              endIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              sx={{
                minWidth: 110,
                borderColor: 'divider',
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: 'transparent',
                  boxShadow: 1,
                },
              }}
            >
              {showMoreFilters ? 'Menos' : 'Más'}
              {activeFiltersCount > 0 && (
                <Chip
                  label={activeFiltersCount}
                  size="small"
                  sx={{
                    ml: 1,
                    backgroundColor: 'success.main',
                    color: 'white',
                  }}
                />
              )}
            </MoreFiltersButton>
          </ButtonsContainer>
        </Grid>
      </FiltersGrid>

      {/* Date validation error */}
      {dateError && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Alert severity="error" onClose={() => {}}>
            {dateError}
          </Alert>
        </Box>
      )}

      {/* Active filters display */}
      {activeFiltersCount > 0 && (
        <ActiveFiltersContainer>
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mr: 1 }}>Filtros activos:</Box>
          {filters.search && (
            <ActiveFiltersChip
              label={`Búsqueda: ${filters.search}`}
              onDelete={() => {
                const newFilters = { ...filters, search: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.eventType && (
            <ActiveFiltersChip
              label={`Tipo: ${EVENT_TYPES.find((e) => e.value === filters.eventType)?.label}`}
              onDelete={() => {
                const newFilters = { ...filters, eventType: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.locationId && (
            <ActiveFiltersChip
              label={`Ubicación: ${Array.isArray(locations) ? locations.find((l) => l.id === filters.locationId)?.name || filters.locationId : filters.locationId}`}
              onDelete={() => {
                const newFilters = { ...filters, locationId: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {canFilterByUser && filters.userId && (
            <ActiveFiltersChip
              label={`Usuario: ${
                Array.isArray(users)
                  ? (() => {
                      const selected = users.find((u) => u.id === filters.userId);
                      return selected ? getUserDisplayName(selected) : filters.userId;
                    })()
                  : filters.userId
              }`}
              onDelete={() => {
                const newFilters = { ...filters, userId: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.serialNumber && (
            <ActiveFiltersChip
              label={`Serie: ${filters.serialNumber}`}
              onDelete={() => {
                const newFilters = { ...filters, serialNumber: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.startDate && (
            <ActiveFiltersChip
              label={`Desde: ${filters.startDate}`}
              onDelete={() => {
                const newFilters = { ...filters, startDate: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.endDate && (
            <ActiveFiltersChip
              label={`Hasta: ${filters.endDate}`}
              onDelete={() => {
                const newFilters = { ...filters, endDate: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.relatedEntityType && (
            <ActiveFiltersChip
              label={`Tipo Entidad: ${RELATED_ENTITY_TYPES.find((t) => t.value === filters.relatedEntityType)?.label || filters.relatedEntityType}`}
              onDelete={() => {
                const newFilters = { ...filters, relatedEntityType: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.transferCode && (
            <ActiveFiltersChip
              label={`Transferencia: ${filters.transferCode}`}
              onDelete={() => {
                const newFilters = { ...filters, transferCode: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.orderCode && (
            <ActiveFiltersChip
              label={`Venta: ${filters.orderCode}`}
              onDelete={() => {
                const newFilters = { ...filters, orderCode: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
          {filters.recordCode && (
            <ActiveFiltersChip
              label={`Registro: ${filters.recordCode}`}
              onDelete={() => {
                const newFilters = { ...filters, recordCode: undefined };
                onFilterChange(newFilters);
              }}
              size="small"
            />
          )}
        </ActiveFiltersContainer>
      )}

      {/* Advanced filters */}
      <Collapse in={showMoreFilters}>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                select
                label="Tipo de Evento"
                name="eventType"
                value={filters.eventType || ''}
                onChange={(e) =>
                  handleEventTypeChange(
                    e.target.value as ProductHistoryListResponseDto.eventType | '',
                  )
                }
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {EVENT_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </StandardTextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                label="Número de Serie"
                name="serialNumber"
                value={filters.serialNumber || ''}
                onChange={handleChange}
                size="small"
                placeholder="Ej: RING-001-240115-001"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                select
                label="Ubicación"
                name="locationId"
                value={filters.locationId || ''}
                onChange={handleSelectChange}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {Array.isArray(locations) &&
                  locations.map((location) => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
              </StandardTextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                select
                label="Tipo de Entidad Relacionada"
                name="relatedEntityType"
                value={filters.relatedEntityType || ''}
                onChange={handleChange}
                size="small"
                InputLabelProps={{ shrink: true }}
              >
                {RELATED_ENTITY_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </StandardTextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                label="Código de Transferencia"
                name="transferCode"
                value={filters.transferCode || ''}
                onChange={handleChange}
                size="small"
                placeholder="Ej: TR-2024-001"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                label="Código de venta"
                name="orderCode"
                value={filters.orderCode || ''}
                onChange={handleChange}
                size="small"
                placeholder="Ej: ORD-2024-001"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                label="Código de Registro"
                name="recordCode"
                value={filters.recordCode || ''}
                onChange={handleChange}
                size="small"
                placeholder="Ej: INV-2024-001"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {canFilterByUser && (
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  fullWidth
                  options={Array.isArray(users) ? users : []}
                  value={selectedUser}
                  onChange={(_event, newValue) => handleUserIdChange(newValue?.id ?? null)}
                  getOptionLabel={(option) => getUserDisplayName(option)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, { inputValue }) => {
                    const query = inputValue.trim().toLowerCase();
                    if (!query) return options;
                    return options.filter((user) => {
                      const name = getUserDisplayName(user).toLowerCase();
                      const email = (user.email || '').toLowerCase();
                      return name.includes(query) || email.includes(query);
                    });
                  }}
                  noOptionsText="Sin coincidencias"
                  ListboxProps={{
                    style: { maxHeight: '200px' },
                  }}
                  renderInput={(params) => (
                    <StandardTextField
                      {...params}
                      label="Usuario"
                      size="small"
                      placeholder="Buscar usuario..."
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                type="date"
                label="Fecha de Inicio"
                name="startDate"
                value={filters.startDate || ''}
                onChange={handleChange}
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!dateError}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <StandardTextField
                fullWidth
                type="date"
                label="Fecha de Fin"
                name="endDate"
                value={filters.endDate || ''}
                onChange={handleChange}
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!dateError}
              />
            </Grid>

            {/* Date range quick selector */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label="Hoy"
                  onClick={() => {
                    const today = new Date().toISOString().split('T')[0];
                    handleChange({
                      target: { name: 'startDate', value: today },
                    } as React.ChangeEvent<HTMLInputElement>);
                    handleChange({
                      target: { name: 'endDate', value: today },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Ayer"
                  onClick={() => {
                    const yesterday = new Date();
                    yesterday.setDate(yesterday.getDate() - 1);
                    const yesterdayStr = yesterday.toISOString().split('T')[0];
                    handleChange({
                      target: { name: 'startDate', value: yesterdayStr },
                    } as React.ChangeEvent<HTMLInputElement>);
                    handleChange({
                      target: { name: 'endDate', value: yesterdayStr },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
                <Chip
                  label="Última semana"
                  onClick={() => {
                    const end = new Date();
                    const start = new Date();
                    start.setDate(start.getDate() - 7);
                    handleChange({
                      target: { name: 'startDate', value: start.toISOString().split('T')[0] },
                    } as React.ChangeEvent<HTMLInputElement>);
                    handleChange({
                      target: { name: 'endDate', value: end.toISOString().split('T')[0] },
                    } as React.ChangeEvent<HTMLInputElement>);
                  }}
                  variant="outlined"
                  size="small"
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </FiltersContainer>
  );
};

export default ProductMovementHistoryFilters;
